import type { WebsiteHeaderEventTitle } from '@shared/types/WebsiteHeaderSettings.types';

// Every tag shape sanitizeEventTitleHtml (the write-time sanitizer) can emit: the whitelisted
// elements, optionally with a quoted style attribute free of angle brackets, plus <br>.
const ALLOWED_TAG_PATTERN = /<(?:\/(?:p|div|span|strong|em|u)|(?:p|div|span|strong|em|u)(?:\s+style="[^"<>]*")?|br\s*\/?)\s*>/gi;

/**
 * Render-time trust check for a header event title's rich HTML (eventName.contentHtml).
 *
 * The HTML is sanitized on the admin write paths, but header dataJSON is schemaless on the
 * backend — any other writer could land unsanitized markup in the guest hero, which is injected
 * via dangerouslySetInnerHTML. This guard rejects anything the title editor could not have
 * produced (unknown tags, non-style attributes, event handlers) so the guest falls back to the
 * plain display name instead of rendering it. Deliberately dependency-free — the `xss` package
 * must stay out of the eager guest header (LCP) chunk. It validates markup shape only; CSS
 * property abuse is bounded by the style-attribute charset and cannot execute script.
 */
export const isSafeEventTitleHtml = (html: string): boolean => !/[<>]/.test(html.replace(ALLOWED_TAG_PATTERN, ''));

/**
 * The single decision for "should this surface render the rich multi-line title?" — rollout flag
 * on, content non-empty, and markup that passes the trust check above. Returns the HTML to inject,
 * or `undefined` to fall back to the plain event display name.
 *
 * Shared so the guest header (`EventNameNav`) and the admin template thumbnail
 * (`BrannanThumbnailContent`) cannot drift. The flag gates the guest render itself, so a tile that
 * resolved this differently would advertise a title the couple's live site does not show. Having
 * one resolver also means retiring the flag after rollout is a single edit, with no silent
 * divergence if the second call site is missed.
 *
 * Kept dependency-free for the same reason as `isSafeEventTitleHtml` — this sits in the eager
 * guest header (LCP) chunk.
 */
export const resolveSafeEventTitleHtml = (title: Maybe<WebsiteHeaderEventTitle>, isMultilineEnabled: boolean): string | undefined =>
  isMultilineEnabled && title?.contentHtml?.trim() && isSafeEventTitleHtml(title.contentHtml) ? title.contentHtml : undefined;
